// Timer — mine økter, sum, ny økt, overlapp-advarsler og attestering.
//
// TRE TING SOM IKKE ER FORHANDLINGSBARE PÅ DENNE SKJERMEN:
//  1. Summen kommer fra `sumTimer` i @skade/dugnad (via `sumTimerFor`), som
//     runder ÉN gang. Ingen `Math.round` på en rad noe sted i fila.
//  2. `advarsler[]` fra `POST /okter` vises alltid, og økta er lagret uansett.
//     Verktøyet vet ikke hvilken av to overlappende føringer som er riktig.
//  3. Ingen knapp tilbys som tjenesten ville svart 403 på: `kanEndre` og
//     `kanAttestere` i lib/okt-lib.ts speiler reglene, og selvattestering
//     tilbys aldri.
import { AKTIVITET_NAVN, timerTekst, type Aktivitetstype, type Status } from '@skade/dugnad';
import type { Kontekst } from './kontekst.js';
import type { OverlappAdvarsel, ØktKropp } from '../api/typer.js';
import {
  grupperPerDag, kanAttestere, kanEndre, lesØkter, sumTekstFor, sumTimerFor,
  tilAttestering, STATUS_NAVN, KILDE_NAVN, type ØktRad,
} from '../lib/okt-lib.js';
import {
  formaterDato, formaterTidsrom, lokaleDeler, somDatoFelt, tolkØktSkjema,
} from '../lib/tid.js';
import { div, felt, h, knapp, merke, p, sett, span, tekstområde, velger } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, tomt } from '../ui/tilstand.js';

const TYPEVALG = (Object.keys(AKTIVITET_NAVN) as Aktivitetstype[])
  .map((v) => ({ verdi: v, navn: AKTIVITET_NAVN[v] }));

const STATUSVALG = (Object.keys(STATUS_NAVN) as Status[])
  .map((v) => ({ verdi: v, navn: STATUS_NAVN[v] }));

/** Første i inneværende måned — et periodefilter folk kjenner igjen. */
function månedsstart(nå: number): string {
  const d = lokaleDeler(nå);
  return `${d.aar}-${String(d.mnd).padStart(2, '0')}-01`;
}

export function visTimer(rot: HTMLElement, ctx: Kontekst): void {
  const nå = Date.now();
  let økter: ØktRad[] = [];

  const side = div('side');
  sett(rot, side);

  // ── Topplinje: tittel og periode ──────────────────────────────────────
  const topp = div('side-topp');
  const titler = div('');
  titler.append(h(1, 'side-tittel', 'Timer'));
  titler.append(p('side-under', 'Egne økter i detalj, andres i offentlig form. '
    + 'Klokkeslett tolkes i norsk tid.'));
  topp.append(titler);

  const fra = felt('Fra', { type: 'date', verdi: månedsstart(nå) });
  const til = felt('Til', { type: 'date', verdi: somDatoFelt(nå) });
  const typefilter = velger('Type', [{ verdi: '', navn: 'Alle typer' }, ...TYPEVALG]);
  const eierfilter = velger('Viser', [
    { verdi: '', navn: 'Alle' }, { verdi: 'egne', navn: 'Bare mine' }, { verdi: 'andres', navn: 'Bare andres' },
  ]);
  const filterrad = div('filterrad');
  filterrad.append(fra.rot, til.rot, typefilter.rot, eierfilter.rot);
  topp.append(filterrad);
  side.append(topp);

  for (const k of [fra.input, til.input, typefilter.select, eierfilter.select]) {
    k.addEventListener('change', () => void hent());
  }

  // ── Oppsummering ──────────────────────────────────────────────────────
  const sumkort = div('sumkort');
  side.append(sumkort);

  // ── Ny økt ────────────────────────────────────────────────────────────
  const skjemakort = div('kort kort-skjema');
  side.append(skjemakort);
  byggSkjema();

  // ── Advarsler fra siste melding ───────────────────────────────────────
  const advarselplass = div('advarselplass');
  side.append(advarselplass);

  // ── Attestering ───────────────────────────────────────────────────────
  const attestplass = div('');
  side.append(attestplass);

  // ── Lista ─────────────────────────────────────────────────────────────
  const listeplass = div('');
  side.append(listeplass);

  void hent();

  // ══ Henting ═══════════════════════════════════════════════════════════
  async function hent(): Promise<void> {
    sett(listeplass, laster('Henter økter …', 4));
    sett(sumkort);
    const sok: Record<string, string | undefined> = {};
    const f = tilMs(fra.input.value, false);
    const t = tilMs(til.input.value, true);
    if (f !== undefined) sok.fra = String(f);
    if (t !== undefined) sok.til = String(t);
    if (typefilter.select.value) sok.type = typefilter.select.value;

    const r = await ctx.klient.økter(sok);
    if (!r.ok) {
      sett(listeplass, feilboks(r.feil, () => void hent()));
      // Summene fjernes ved feil. Å la forrige periodes tall stå ville vært
      // tall uten dekning i det som faktisk er hentet.
      sett(sumkort, p('sumkort-tomt', 'Ingen sum — øktene kunne ikke hentes.'));
      return;
    }
    økter = lesØkter(r.data);
    tegn();
  }

  function tilMs(datofelt: string, sluttPåDagen: boolean): number | undefined {
    if (!datofelt) return undefined;
    const r = tolkØktSkjema(datofelt, '00:00', sluttPåDagen ? '23:59' : '00:01');
    if (!r.ok) return undefined;
    return sluttPåDagen ? r.verdi.startTsMs + r.verdi.minutter * 60_000 : r.verdi.startTsMs;
  }

  function tegn(): void {
    const eier = eierfilter.select.value;
    const vist = økter.filter((o) => (eier === 'egne' ? o.egen : eier === 'andres' ? !o.egen : true));
    const mine = økter.filter((o) => o.egen);

    tegnSum(vist, mine);
    tegnAttestering();
    tegnListe(vist);
  }

  function tegnSum(vist: readonly ØktRad[], mine: readonly ØktRad[]): void {
    sett(sumkort);
    sumkort.append(sumboks('Mine timer i perioden', `${sumTimerFor(mine)} t`, sumTekstFor(mine)));
    sumkort.append(sumboks('Mine økter', String(mine.length), mine.length === 1 ? 'én føring' : 'føringer'));
    sumkort.append(sumboks('Viste økter', String(vist.length),
      `${vist.filter((o) => !o.egen).length} av andre`));
    const venter = mine.filter((o) => o.status === 'meldt').length;
    sumkort.append(sumboks('Mine til attestering', String(venter),
      venter ? 'venter på en annens signatur' : 'ingenting venter'));
  }

  function sumboks(etikett: string, stor: string, under: string): HTMLElement {
    const b = div('sumboks');
    b.append(span('sumboks-etikett', etikett));
    b.append(span('sumboks-tall', stor));
    b.append(span('sumboks-under', under));
    return b;
  }

  // ══ Skjema for ny økt ═════════════════════════════════════════════════
  function byggSkjema(): void {
    sett(skjemakort);
    const hode = div('kort-hode');
    hode.append(ikon('pluss'), h(2, 'kort-tittel', 'Meld ny økt'));
    skjemakort.append(hode);

    const skjema = document.createElement('form');
    skjema.className = 'skjema-rutenett';
    skjema.noValidate = true;

    const dato = felt('Dato', { type: 'date', verdi: somDatoFelt(nå), påkrevd: true });
    const fraKl = felt('Fra kl.', { type: 'time', verdi: '18:00', påkrevd: true });
    const tilKl = felt('Til kl.', { type: 'time', verdi: '20:00', påkrevd: true });
    const type = velger('Aktivitet', TYPEVALG, 'ovelse');
    const hva = tekstområde('Hva ble gjort', 'Kort beskrivelse — synlig for deg og den som attesterer.', 2);
    const bolge = felt('Bølge / prosjekt', { plassholder: 'valgfritt' });
    const oppdrag = felt('Oppdragskode', { plassholder: 'valgfritt' });
    const status = velger('Lagre som', [
      { verdi: 'meldt', navn: 'Meldt (klar til attestering)' },
      { verdi: 'utkast', navn: 'Utkast (ikke meldt inn ennå)' },
    ], 'meldt');

    const varighet = span('varighet-hint', '');
    const oppdaterVarighet = (): void => {
      const r = tolkØktSkjema(dato.input.value, fraKl.input.value, tilKl.input.value);
      varighet.textContent = r.ok
        ? `Varighet: ${timerTekst(r.verdi.minutter)}${r.merknader.length ? ' · ' + r.merknader.join(' ') : ''}`
        : '';
      varighet.classList.toggle('varsel', r.ok && r.merknader.length > 0);
    };
    for (const i of [dato.input, fraKl.input, tilKl.input]) i.addEventListener('input', oppdaterVarighet);
    oppdaterVarighet();

    const send = knapp('Meld økt', undefined, { variant: 'primær', type: 'submit' });
    const skjemafeil = div('skjema-feil');
    skjemafeil.setAttribute('role', 'alert');
    skjemafeil.hidden = true;

    skjema.append(dato.rot, fraKl.rot, tilKl.rot, type.rot);
    const bred = div('skjema-bred');
    bred.append(hva.rot);
    skjema.append(bred);
    skjema.append(bolge.rot, oppdrag.rot, status.rot);
    const fot = div('skjema-fot');
    fot.append(varighet, send);
    skjema.append(fot);
    skjema.append(skjemafeil);
    skjemakort.append(skjema);

    skjema.addEventListener('submit', (e) => { e.preventDefault(); void meld(); });

    async function meld(): Promise<void> {
      for (const f of [dato, fraKl, tilKl]) f.settFeil(null);
      skjemafeil.hidden = true;

      const tolket = tolkØktSkjema(dato.input.value, fraKl.input.value, tilKl.input.value);
      if (!tolket.ok) {
        const mål = { dato, fra: fraKl, til: tilKl }[tolket.feil.felt as 'dato' | 'fra' | 'til'];
        mål?.settFeil(tolket.feil.grunn);
        mål?.input.focus();
        return;
      }

      const kropp: ØktKropp = {
        type: type.select.value as Aktivitetstype,
        startTsMs: tolket.verdi.startTsMs,
        minutter: tolket.verdi.minutter,
        status: status.select.value as 'meldt' | 'utkast',
      };
      const hvaV = hva.ta.value.trim();
      if (hvaV) kropp.hva = hvaV;
      const b = bolge.input.value.trim();
      if (b) kropp.bolge = b;
      const ok = oppdrag.input.value.trim();
      if (ok) kropp.oppdragskode = ok;

      send.disabled = true;
      const før = send.textContent;
      send.textContent = 'Melder …';
      const r = await ctx.klient.meldØkt(kropp);
      send.disabled = false;
      send.textContent = før;

      if (!r.ok) {
        skjemafeil.hidden = false;
        skjemafeil.textContent = r.feil.melding;
        return;
      }
      hva.ta.value = '';
      ctx.kvitter('Økt meldt');
      ctx.kunngjør(`Økt meldt: ${timerTekst(tolket.verdi.minutter)} ${AKTIVITET_NAVN[kropp.type].toLowerCase()}.`);
      visAdvarsler(r.data?.advarsler ?? [], tolket.merknader);
      await hent();
    }
  }

  /**
   * Overlapp-advarslene. De kommer ETTER at økta er lagret, og skal aldri
   * skjules eller ryddes bort automatisk — se kontrakten, «Hva klienten IKKE
   * skal gjøre».
   */
  function visAdvarsler(advarsler: readonly OverlappAdvarsel[], merknader: readonly string[]): void {
    sett(advarselplass);
    if (!advarsler.length && !merknader.length) return;

    const boks = div('advarsel');
    boks.setAttribute('role', 'alert');
    const hode = div('advarsel-hode');
    hode.append(ikon('varsel'));
    hode.append(h(2, 'advarsel-tittel',
      advarsler.length ? `Økta er lagret — men den overlapper ${advarsler.length === 1 ? 'en annen føring' : `${advarsler.length} andre føringer`}` : 'Økta er lagret'));
    boks.append(hode);

    for (const m of merknader) boks.append(p('advarsel-linje', m));

    for (const a of advarsler) {
      // Tjenestens `melding` er ferdig skrevet på norsk og nevner både varighet,
      // type, kilde og status. Vi viser den ordrett framfor å skrive vår egen.
      boks.append(p('advarsel-linje', a.melding));
    }
    if (advarsler.length) {
      boks.append(p('advarsel-fot',
        'Begge føringene står. Verktøyet vet ikke hvilken som er riktig — '
        + 'rett opp den som er feil i lista under, eller la begge stå hvis de er det.'));
    }
    boks.append(knapp('Lukk', () => sett(advarselplass), { variant: 'stille', ikon: ikon('kryss') }));
    advarselplass.append(boks);
  }

  // ══ Attestering ═══════════════════════════════════════════════════════
  function tegnAttestering(): void {
    sett(attestplass);
    if (!ctx.meg.person.kanAttestere) return;
    const venter = tilAttestering(økter, true);

    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('sjekk'), h(2, 'kort-tittel', 'Til attestering'));
    hode.append(merke(String(venter.length), venter.length ? 'gull' : ''));
    kort.append(hode);

    if (!venter.length) {
      kort.append(p('kort-brød', 'Ingen av andres økter i denne perioden venter på attestering.'));
      attestplass.append(kort);
      return;
    }
    kort.append(p('kort-brød', 'Du kan ikke attestere dine egne økter — det er hele poenget med '
      + 'at noen andre signerer.'));

    for (const o of venter) kort.append(attestrad(o));
    attestplass.append(kort);
  }

  function attestrad(o: ØktRad): HTMLElement {
    const rad = div('attestrad');
    const venstre = div('attestrad-tekst');
    venstre.append(span('attestrad-person', o.personId));
    venstre.append(span('attestrad-detalj',
      `${AKTIVITET_NAVN[o.type]} · ${formaterDato(o.startTsMs)} ${formaterTidsrom(o.startTsMs, o.minutter)} · ${timerTekst(o.minutter)}`));
    if (o.hva) venstre.append(span('attestrad-hva', o.hva));
    rad.append(venstre);

    const knapper = div('attestrad-knapper');
    knapper.append(knapp('Godkjenn', () => void avgjør(o, 'godkjent'), { variant: 'primær', ikon: ikon('sjekk') }));
    knapper.append(knapp('Avvis', () => void avgjør(o, 'avvist'), { variant: 'fare', ikon: ikon('avvis') }));
    rad.append(knapper);
    return rad;
  }

  async function avgjør(o: ØktRad, vedtak: 'godkjent' | 'avvist'): Promise<void> {
    let begrunnelse: string | undefined;
    if (vedtak === 'avvist') {
      // En avvisning uten grunn er en beskjed den andre ikke kan gjøre noe med.
      const svar = globalThis.prompt('Kort begrunnelse for avvisningen (vises til den som meldte):', '');
      if (svar === null) return;
      begrunnelse = svar.trim() || undefined;
    }
    const r = await ctx.klient.attester(o.id, vedtak, begrunnelse);
    if (!r.ok) return ctx.kvitter(r.feil.melding, 'feil');
    ctx.kvitter(vedtak === 'godkjent' ? 'Økt godkjent' : 'Økt avvist');
    ctx.kunngjør(`Økt ${o.id} ${vedtak}.`);
    await hent();
  }

  // ══ Lista ═════════════════════════════════════════════════════════════
  function tegnListe(vist: readonly ØktRad[]): void {
    sett(listeplass);
    if (!vist.length) {
      listeplass.append(tomt(
        'Ingen økter i perioden',
        'Utvid perioden over, eller meld den første økta i skjemaet.',
        { ikon: 'kalender' },
      ));
      return;
    }
    for (const dag of grupperPerDag(vist)) {
      const bolk = div('dagbolk');
      const dagHode = div('dag-hode');
      dagHode.append(span('dag-navn', formaterDato(dag.tsMs)));
      dagHode.append(span('dag-sum', `${dag.timer} t`));
      bolk.append(dagHode);
      for (const o of dag.økter) bolk.append(øktrad(o));
      listeplass.append(bolk);
    }
  }

  function øktrad(o: ØktRad): HTMLElement {
    const rad = div(`øktrad${o.egen ? ' egen' : ''}`);

    const tid = div('øktrad-tid');
    tid.append(span('øktrad-klokke', formaterTidsrom(o.startTsMs, o.minutter)));
    tid.append(span('øktrad-lengde', timerTekst(o.minutter)));
    rad.append(tid);

    const midt = div('øktrad-midt');
    midt.append(span('øktrad-type', AKTIVITET_NAVN[o.type]));
    if (o.hva) midt.append(span('øktrad-hva', o.hva));
    if (!o.egen) midt.append(span('øktrad-person', o.personId));
    const merker = div('øktrad-merker');
    merker.append(merke(STATUS_NAVN[o.status], statusvariant(o.status)));
    merker.append(merke(KILDE_NAVN[o.kilde]));
    if (o.bolge) merker.append(merke(o.bolge));
    if (o.oppdragskode) merker.append(merke(`Kode ${o.oppdragskode}`));
    if (o.attestertAv) merker.append(merke(`Attestert av ${o.attestertAv}`, 'sage'));
    midt.append(merker);
    rad.append(midt);

    const handlinger = div('øktrad-handlinger');
    if (kanEndre(o)) handlinger.append(knapp('Endre', () => visEndre(o, rad), { variant: 'stille' }));
    if (kanAttestere(o, ctx.meg.person.kanAttestere)) {
      handlinger.append(knapp('Godkjenn', () => void avgjør(o, 'godkjent'), { variant: 'stille', ikon: ikon('sjekk') }));
    }
    rad.append(handlinger);
    return rad;
  }

  /** Endring i rad — bare de feltene `PATCH /okter/:id` faktisk tar imot. */
  function visEndre(o: ØktRad, rad: HTMLElement): void {
    const skjema = document.createElement('form');
    skjema.className = 'endre-skjema';
    skjema.noValidate = true;

    const dato = felt('Dato', { type: 'date', verdi: somDatoFelt(o.startTsMs) });
    const fraKl = felt('Fra kl.', { type: 'time', verdi: klokkeAv(o.startTsMs) });
    const tilKl = felt('Til kl.', { type: 'time', verdi: klokkeAv(o.startTsMs + o.minutter * 60_000) });
    const type = velger('Aktivitet', TYPEVALG, o.type);
    const hva = felt('Hva ble gjort', { verdi: o.hva ?? '' });
    const statusV = velger('Status', STATUSVALG.filter((s) => s.verdi === 'utkast' || s.verdi === 'meldt'), o.status);
    const feilEl = div('skjema-feil');
    feilEl.setAttribute('role', 'alert');
    feilEl.hidden = true;

    const lagre = knapp('Lagre', undefined, { variant: 'primær', type: 'submit' });
    const avbryt = knapp('Avbryt', () => skjema.remove(), { variant: 'stille' });
    const fot = div('skjema-fot');
    fot.append(avbryt, lagre);

    skjema.append(dato.rot, fraKl.rot, tilKl.rot, type.rot, hva.rot, statusV.rot, fot, feilEl);
    rad.append(skjema);
    dato.input.focus();

    skjema.addEventListener('submit', (e) => {
      e.preventDefault();
      void lagreEndring();
    });

    async function lagreEndring(): Promise<void> {
      const tolket = tolkØktSkjema(dato.input.value, fraKl.input.value, tilKl.input.value);
      if (!tolket.ok) {
        feilEl.hidden = false;
        feilEl.textContent = tolket.feil.grunn;
        return;
      }
      lagre.disabled = true;
      const r = await ctx.klient.endreØkt(o.id, {
        type: type.select.value as Aktivitetstype,
        startTsMs: tolket.verdi.startTsMs,
        minutter: tolket.verdi.minutter,
        hva: hva.input.value.trim(),
        status: statusV.select.value as 'meldt' | 'utkast',
      });
      lagre.disabled = false;
      if (!r.ok) {
        feilEl.hidden = false;
        feilEl.textContent = r.feil.melding;
        return;
      }
      ctx.kvitter('Økt endret');
      visAdvarsler(r.data?.advarsler ?? [], tolket.merknader);
      await hent();
    }
  }
}

function statusvariant(s: Status): string {
  return s === 'godkjent' ? 'sage' : s === 'avvist' ? 'fare' : s === 'meldt' ? 'gull' : '';
}

function klokkeAv(ts: number): string {
  const d = lokaleDeler(ts);
  return `${String(d.time).padStart(2, '0')}:${String(d.min).padStart(2, '0')}`;
}

